import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  MenuProps
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addMonths } from 'date-fns';
import { User } from 'lucide-react';
import { WorkstreamFormData, Workstream, Assignee, isStatusType } from './types';
import { useStatuses } from './hooks/useStatuses';
import { useWorkstreamTypes } from './hooks/useWorkstreamTypes';
import { useWorkspaceContext } from './context/WorkspaceContext';

interface WorkstreamDialogProps {
  onSubmit: (data: WorkstreamFormData) => Promise<void>;
  initialData?: Workstream | undefined;
  mode: 'add' | 'edit';
  parentWorkstreams: Workstream[];
  assignees: Assignee[];
}

const menuProps: Partial<MenuProps> = {
  sx: { zIndex: 1400 },
  PaperProps: {
    sx: {
      maxHeight: 300,
    }
  },
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const,
  },
};

const datePickerSx = { 
  '& .MuiPaper-root': { 
    zIndex: 1400 
  } 
};

export const WorkstreamDialog: React.FC<WorkstreamDialogProps> = ({
  onSubmit,
  initialData,
  mode,
  parentWorkstreams = [],
  assignees = []
}) => {
  const { workspaceId } = useWorkspaceContext();
  const { statuses } = useStatuses(workspaceId);
  const { workstreamTypes: dynamicWorkstreamTypes } = useWorkstreamTypes(workspaceId);

  // Define initial form state using hook values
  const initialFormState: WorkstreamFormData = {
    name: '',
    startDate: new Date(),
    endDate: addMonths(new Date(), 1),
    type: dynamicWorkstreamTypes[0]?.name || 'FEATURE',
    status: statuses[0]?.name || 'PLANNED',
    description: '',
    parentId: null,
    isParent: false,
    assigneeId: null
  };

  // State hooks
  const [formData, setFormData] = useState<WorkstreamFormData>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const defaultFormState: WorkstreamFormData = {
      name: '',
      startDate: new Date(),
      endDate: addMonths(new Date(), 1),
      type: dynamicWorkstreamTypes[0]?.name || 'FEATURE',
      status: statuses[0]?.name || 'PLANNED',
      description: '',
      parentId: null,
      isParent: false,
      assigneeId: null
    };
  
    if (initialData) {
      setFormData({
        name: initialData.name,
        startDate: new Date(initialData.startDate),
        endDate: new Date(initialData.endDate),
        type: initialData.type,
        status: initialData.status,
        description: initialData.description || '',
        parentId: initialData.parentId || null,
        isParent: initialData.isParent || false,
        assigneeId: initialData.assigneeId || null
      });
    } else {
      setFormData(defaultFormState);
    }
  }, [initialData, dynamicWorkstreamTypes, statuses]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting workstream:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWorkstreamTypeChange = (event: any) => {
    const isParent = event.target.value === 'parent';
    setFormData(prev => ({
      ...prev,
      isParent,
      parentId: isParent ? null : (prev.parentId || null)
    }));
  };

  const handleAssigneeChange = (event: any) => {
    const assigneeId = event.target.value || null;
    setFormData(prev => ({
      ...prev,
      assigneeId
    }));
  };

  const handleTypeChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      type: event.target.value as string
    }));
  };

  const handleStatusChange = (event: any) => {
    const selectedStatus = statuses.find(s => s.name === event.target.value);
    if (selectedStatus && isStatusType(selectedStatus)) {
      setFormData(prev => ({
        ...prev,
        status: selectedStatus.name
      }));
    }
  };

  const handleParentChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      parentId: event.target.value || null
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="flex flex-col h-full">
        <h2 className="text-2xl font-semibold mb-8 pt-4">
          {mode === 'add' ? 'Add New Workstream' : 'Edit Workstream'}
        </h2>
        
        <div className="flex-1 overflow-y-auto px-1" style={{ overflowX: 'visible' }}>
          <div className="space-y-8">
            {/* Name field with increased bottom margin */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Workstream Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                disabled={mode === 'edit'}
                error={!formData.name.trim()}
                helperText={!formData.name.trim() ? 'Name is required' : ''}
              />
            </Box>
            
            {/* Type selection with increased spacing */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={handleTypeChange}
                  MenuProps={menuProps}
                >
                  {dynamicWorkstreamTypes.map((type) => (
                    <MenuItem key={type.id} value={type.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: type.color
                          }}
                        />
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Workstream Type selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Workstream Type</InputLabel>
                <Select
                  value={formData.isParent ? 'parent' : 'child'}
                  label="Workstream Type"
                  onChange={handleWorkstreamTypeChange}
                  MenuProps={menuProps}
                >
                  <MenuItem value="child">Child Workstream</MenuItem>
                  <MenuItem value="parent">Parent Workstream</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Conditional Parent Workstream selection */}
            {!formData.isParent && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Parent Workstream</InputLabel>
                  <Select
                    value={formData.parentId || ''}
                    label="Parent Workstream"
                    onChange={handleParentChange}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">None</MenuItem>
                    {parentWorkstreams.map((parent) => (
                      <MenuItem key={parent.id} value={parent.id}>
                        {parent.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Status selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleStatusChange}
                  MenuProps={menuProps}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.id} value={status.name}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Assignee selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={formData.assigneeId || ''}
                  label="Assignee"
                  onChange={handleAssigneeChange}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <User size={16} />
                      Unassigned
                    </Box>
                  </MenuItem>
                  {assignees.map((assignee) => (
                    <MenuItem key={assignee.id} value={assignee.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {assignee.avatarUrl ? (
                          <Avatar src={assignee.avatarUrl} sx={{ width: 24, height: 24 }} />
                        ) : (
                          <User size={16} />
                        )}
                        {assignee.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Date selection group */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(date) => date && setFormData(prev => ({ ...prev, startDate: date }))}
                sx={datePickerSx}
              />
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(date) => date && setFormData(prev => ({ ...prev, endDate: date }))}
                sx={datePickerSx}
              />
            </Box>

            {/* Description field */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Box>
          </div>
        </div>
        
        {/* Footer with action buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          mt: 4, 
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'relative'
        }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || !formData.name.trim()}
          >
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add Workstream' : 'Save Changes'}
          </Button>
        </Box>
      </div>
    </LocalizationProvider>
  );
};